import pick from 'lodash-es/pick'

import type { useAnalyticsChartQuery } from '@fourthwall/module-analytics'
import {
  SUMMARY_ROW_ID,
  createAllRangeDates,
  getFromMapByTimestamp,
  mapCurrentDataToTimestampsMap,
} from '@fourthwall/module-analytics'

import type { AnalyticsParams } from '../../../../../modules/AnalyticsParams'
import type { AnalyticsReportStatsTable } from '../AnalyticsReport.types'

export const createChartStatsTableData = (
  current:
    | NonNullable<
        ReturnType<typeof useAnalyticsChartQuery>['analyticsChartQuery']['data']
      >['current']
    | undefined,
  params: AnalyticsParams,
  keys: (keyof NonNullable<
    ReturnType<typeof useAnalyticsChartQuery>['analyticsChartQuery']['data']
  >['current'])[],
): AnalyticsReportStatsTable<any>['data'] => {
  if (!current) return []

  const allRangeDates = createAllRangeDates(params).reverse()

  const averageProfit = mapCurrentDataToTimestampsMap(current.averageProfit?.data)
  const averageSales = mapCurrentDataToTimestampsMap(current.averageSales?.data)
  const cancellations = mapCurrentDataToTimestampsMap(current.cancellations?.data)
  const contribution = mapCurrentDataToTimestampsMap(current.contribution?.data)
  const contributionDonations = mapCurrentDataToTimestampsMap(current.contributionDonations?.data)
  const contributionGiftsOnCreatorCost = mapCurrentDataToTimestampsMap(
    current.contributionGiftsOnCreatorCost?.data,
  )
  const contributionGiveaway = mapCurrentDataToTimestampsMap(current.contributionGiveaway?.data)
  const contributionOrders = mapCurrentDataToTimestampsMap(current.contributionOrders?.data)
  const contributionSubscriptions = mapCurrentDataToTimestampsMap(
    current.contributionSubscriptions?.data,
  )
  const contributionMembershipTips = mapCurrentDataToTimestampsMap(
    current.contributionMembershipTips?.data,
  )
  const contributionMembershipLockedMessages = mapCurrentDataToTimestampsMap(
    current.contributionMembershipLockedMessages?.data,
  )
  const contributionTotal = mapCurrentDataToTimestampsMap(current.contributionTotal?.data)
  const costs = mapCurrentDataToTimestampsMap(current.cost?.data)
  const discounts = mapCurrentDataToTimestampsMap(current.discounts?.data)
  const grossSales = mapCurrentDataToTimestampsMap(current.grossSales?.data)
  const profits = mapCurrentDataToTimestampsMap(current.profit?.data)
  const refunds = mapCurrentDataToTimestampsMap(current.refunds?.data)
  const values = mapCurrentDataToTimestampsMap(current.income?.data)

  const statsTableData = allRangeDates.map((date) => ({
    id: date,
    day: date,

    averageProfit: getFromMapByTimestamp(averageProfit, date) || 0,
    averageSales: getFromMapByTimestamp(averageSales, date) || 0,
    cancellations: getFromMapByTimestamp(cancellations, date) || 0,
    contribution: getFromMapByTimestamp(contribution, date) || 0,
    contributionDonations: getFromMapByTimestamp(contributionDonations, date) || 0,
    contributionGiftsOnCreatorCost:
      getFromMapByTimestamp(contributionGiftsOnCreatorCost, date) || 0,
    contributionGiveaway: getFromMapByTimestamp(contributionGiveaway, date) || 0,
    contributionOrders: getFromMapByTimestamp(contributionOrders, date) || 0,
    contributionSubscriptions: getFromMapByTimestamp(contributionSubscriptions, date) || 0,
    contributionMembershipTips: getFromMapByTimestamp(contributionMembershipTips, date) || 0,
    contributionMembershipLockedMessages:
      getFromMapByTimestamp(contributionMembershipLockedMessages, date) || 0,
    contributionTotal: getFromMapByTimestamp(contributionTotal, date) || 0,
    cost: getFromMapByTimestamp(costs, date) || 0,
    discounts: getFromMapByTimestamp(discounts, date) || 0,
    grossSales: getFromMapByTimestamp(grossSales, date) || 0,
    profit: getFromMapByTimestamp(profits, date) || 0,
    refunds: getFromMapByTimestamp(refunds, date) || 0,
    value: getFromMapByTimestamp(values, date) || 0,
  }))

  statsTableData.unshift({
    id: SUMMARY_ROW_ID,
    day: 'Summary',

    averageProfit: current.averageProfitValue?.value || 0,
    // averageProfit: current.averageProfit?.aggregatedValue || 0,
    averageSales: current.averageGrossSalesValue?.value || 0,
    // averageSales: current.averageSales?.aggregatedValue || 0,
    cancellations: current.cancellations?.aggregatedValue || 0,
    contribution: current.contribution?.aggregatedValue || 0,
    contributionDonations: current.contributionDonations?.aggregatedValue || 0,
    contributionGiftsOnCreatorCost: current.contributionGiftsOnCreatorCost?.aggregatedValue || 0,
    contributionGiveaway: current.contributionGiveaway?.aggregatedValue || 0,
    contributionOrders: current.contributionOrders?.aggregatedValue || 0,
    contributionSubscriptions: current.contributionSubscriptions?.aggregatedValue || 0,
    contributionMembershipTips: current.contributionMembershipTips?.aggregatedValue || 0,
    contributionMembershipLockedMessages:
      current.contributionMembershipLockedMessages?.aggregatedValue || 0,
    contributionTotal: current.contributionTotal?.aggregatedValue || 0,
    cost: current.cost?.aggregatedValue || 0,
    discounts: current.discounts?.aggregatedValue || 0,
    grossSales: current.grossSales?.aggregatedValue || 0,
    profit: current.profit?.aggregatedValue || 0,
    refunds: current.refunds?.aggregatedValue || 0,
    value: current.income?.aggregatedValue || 0,
  })

  // TODO: Cleanup and tests.
  return statsTableData.map((item) => {
    if (!keys || !keys.length) return item

    return pick(item, ['id', 'day', ...keys])
  })
}
